import { cloneElement, createContext, isValidElement, ReactNode, useCallback, useContext, useEffect, useId, useState } from 'react';
import type { EmailStatus, TimelineEvent } from '../lib/api';
import { customerEvents, EVENT_LABEL, eventExplanation, eventTone, ms, serverReply, STATUS_HINT, STATUS_LABEL } from '../lib/format';
import { currentLocale, t } from '../lib/i18n';
import { IconCheck, IconCopy, IconX } from './icons';

// ---------- toasts ----------

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'bad';
}
const ToastCtx = createContext<(text: string, tone?: 'ok' | 'bad') => void>(() => undefined);
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: 'ok' | 'bad' = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-stack" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast${t.tone === 'bad' ? ' bad' : ''}`}>{t.text}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

// ---------- data loading ----------

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fn()
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e: Error) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1), setData };
}

// ---------- primitives ----------

export function StatusPill({ status }: { status: EmailStatus }) {
  return (
    <span className={`pill ${status}`} title={STATUS_HINT[status] ? t(STATUS_HINT[status]) : undefined}>
      {STATUS_LABEL[status] ? t(STATUS_LABEL[status]) : status}
    </span>
  );
}

export function CopyButton({ value, label, small, track }: { value: string; label?: string; small?: boolean; track?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className={`btn ${small ? 'btn-sm' : ''}`}
      data-track={track}
      onClick={async () => {
        await navigator.clipboard.writeText(value);
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <IconCheck size={15} /> : <IconCopy size={15} />}
      {done ? t('Скопировано') : (label ?? t('Копировать'))}
    </button>
  );
}

export function Modal({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal card" role="dialog" aria-modal="true" aria-label={title}>
        <div className="card-head">
          <h3>{title}</h3>
          <button className="btn btn-ghost icon-btn" onClick={onClose} aria-label={t('Закрыть')}><IconX /></button>
        </div>
        <div className="card-pad stack">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, error, htmlFor, children }: { label: string; hint?: ReactNode; error?: string; htmlFor?: string; children: ReactNode }) {
  const generated = useId();
  // A single form control gets an id, so the label names it for screen readers and clicking the label focuses it.
  const control = isValidElement<{ id?: string }>(children) && ['input', 'textarea', 'select'].includes(children.type as string) ? children : null;
  // A control nested deeper (an input next to its button) is named by `htmlFor` with the input's own id.
  const id = control ? (control.props.id ?? generated) : htmlFor;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {control ? cloneElement(control, { id }) : children}
      {error ? <span className="err">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon}
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Skeleton({ h = 18, w = '100%' }: { h?: number; w?: number | string }) {
  return <div className="skeleton" style={{ height: h, width: w }} />;
}

// ---------- code snippets ----------

export function CodeTabs({ snippets }: { snippets: Record<string, string> }) {
  const names = Object.keys(snippets);
  const [on, setOn] = useState(names[0]);
  return (
    <div>
      <div className="tabs row">
        {names.map((n) => (
          <button key={n} className={`tab${n === on ? ' on' : ''}`} onClick={() => setOn(n)}>{n}</button>
        ))}
        <span className="spacer" />
        <CopyButton small value={snippets[on]} />
      </div>
      <pre className="code">{snippets[on]}</pre>
    </div>
  );
}

export function sendSnippets(key: string, from: string, to = 'user@example.com') {
  const host = window.location.origin;
  const subject = t('Ваш заказ оплачен');
  const html = t('<p>Спасибо за покупку!</p>');
  const dup = t('повтор не создаст дубль');
  return {
    cURL: `curl -X POST ${host}/v1/emails \\
  -H "Authorization: Bearer ${key}" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-1042" \\
  -d '{
    "from": "${from}",
    "to": "${to}",
    "subject": "${subject}",
    "html": "${html}"
  }'`,
    'Node.js': `const res = await fetch('${host}/v1/emails', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer ${key}',
    'Content-Type': 'application/json',
    'Idempotency-Key': \`order-\${order.id}\`, // ${dup}
  },
  body: JSON.stringify({
    from: '${from}',
    to: '${to}',
    subject: '${subject}',
    html: '${html}',
  }),
});
const { id, status } = await res.json();`,
    Python: `import requests

r = requests.post(
    "${host}/v1/emails",
    headers={
        "Authorization": "Bearer ${key}",
        "Idempotency-Key": f"order-{order.id}",
    },
    json={
        "from": "${from}",
        "to": "${to}",
        "subject": "${subject}",
        "html": "${html}",
    },
)
print(r.json())  # {'id': 'msg_…', 'status': 'queued'}`,
  };
}

// ---------- timeline: the dive ----------

export function Dive({ events }: { events: TimelineEvent[] }) {
  return (
    <div className="dive">
      {customerEvents(events).map((e, i) => (
        <div key={i} className={`dive-item ${eventTone(e.type)}`} style={{ animationDelay: `${i * 70}ms` }}>
          <div className="row">
            <span className="t">{EVENT_LABEL[e.type] ? t(EVENT_LABEL[e.type]) : e.type}</span>
            <span className="spacer" />
            <span className="ms">+{ms(e.elapsed_ms)}</span>
          </div>
          <div className="m">
            {new Date(e.at).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            .{String(new Date(e.at).getMilliseconds()).padStart(3, '0')}
            {i > 0 && <> · {t('шаг')} {ms(e.delta_ms)}</>}
            {e.recipient && <> · {e.recipient}</>}
            {typeof e.data.error === 'string' && <> · {e.data.error}</>}
          </div>
          {eventExplanation(e).map((line) => (
            <div key={line} className="m dive-explain">{line}</div>
          ))}
          {serverReply(e.data) && <div className="m dive-reply">{t('Ответ сервера:')} {serverReply(e.data)}</div>}
        </div>
      ))}
    </div>
  );
}

// ---------- charts ----------

/** Volume as a tide: smooth area for all mail with the problem share drawn as a darker swell. */
export function TideChart({ series }: { series: { day: string; total: number; bounced: number; failed: number }[] }) {
  const W = 720;
  const H = 220;
  const pad = { l: 36, r: 12, t: 16, b: 28 };
  const max = Math.max(4, ...series.map((s) => s.total));
  const x = (i: number) => pad.l + (i * (W - pad.l - pad.r)) / Math.max(1, series.length - 1);
  const y = (v: number) => H - pad.b - (v / max) * (H - pad.t - pad.b);
  const smooth = (vals: number[]) =>
    vals
      .map((v, i) => {
        if (i === 0) return `M ${x(0)} ${y(v)}`;
        const cx = (x(i - 1) + x(i)) / 2;
        return `C ${cx} ${y(vals[i - 1])}, ${cx} ${y(v)}, ${x(i)} ${y(v)}`;
      })
      .join(' ');
  const total = series.map((s) => s.total);
  const bad = series.map((s) => s.bounced + s.failed);
  const area = (vals: number[]) => `${smooth(vals)} L ${x(vals.length - 1)} ${H - pad.b} L ${x(0)} ${H - pad.b} Z`;
  const ticks = [0, Math.round(max / 2), max];
  const [hover, setHover] = useState<number | null>(null);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={t('Объём писем по дням')} onMouseLeave={() => setHover(null)}>
      <defs>
        <linearGradient id="tide" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#3aa0ff" stopOpacity="0.45" />
          <stop offset="1" stopColor="#3aa0ff" stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="swell" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f2605a" stopOpacity="0.45" />
          <stop offset="1" stopColor="#f2605a" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={pad.l} x2={W - pad.r} y1={y(t)} y2={y(t)} stroke="var(--border)" strokeDasharray="3 5" />
          <text x={pad.l - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fill="var(--muted)">{t}</text>
        </g>
      ))}
      <path d={area(total)} fill="url(#tide)" />
      <path d={smooth(total)} fill="none" stroke="var(--accent)" strokeWidth="2.5" />
      <path d={area(bad)} fill="url(#swell)" />
      <path d={smooth(bad)} fill="none" stroke="#f2605a" strokeWidth="1.6" strokeDasharray="4 4" />
      {series.map((s, i) => (
        <g key={s.day}>
          {(i % Math.ceil(series.length / 7) === 0 || i === series.length - 1) && (
            <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="var(--muted)">
              {new Date(s.day).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'short' })}
            </text>
          )}
          <rect x={x(i) - 14} y={pad.t} width="28" height={H - pad.t - pad.b} fill="transparent" onMouseEnter={() => setHover(i)} />
        </g>
      ))}
      {hover !== null && (
        <g>
          <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={H - pad.b} stroke="var(--accent)" strokeOpacity="0.4" />
          <circle cx={x(hover)} cy={y(total[hover])} r="5" fill="var(--surface)" stroke="var(--accent)" strokeWidth="2.5" />
          <g transform={`translate(${Math.min(x(hover) + 10, W - 150)}, ${pad.t + 4})`}>
            <rect width="140" height="54" rx="8" fill="var(--surface)" stroke="var(--border-strong)" />
            <text x="10" y="18" fontSize="11" fill="var(--muted)">{new Date(series[hover].day).toLocaleDateString(currentLocale(), { day: 'numeric', month: 'long' })}</text>
            <text x="10" y="34" fontSize="12" fontWeight="700" fill="var(--text)">{t('Писем: {n}', { n: total[hover] })}</text>
            <text x="10" y="48" fontSize="11" fill="#f2605a">{t('Проблемы: {n}', { n: bad[hover] })}</text>
          </g>
        </g>
      )}
    </svg>
  );
}

/** Health as sea level filling a porthole. */
export function HealthRing({ value }: { value: number }) {
  const r = 64;
  const c = 2 * Math.PI * r;
  const color = value >= 85 ? '#18b98e' : value >= 60 ? '#f5b83d' : '#f2605a';
  const label = value >= 85 ? 'Штиль' : value >= 60 ? 'Волнение' : 'Шторм';
  // The label is a Russian key; t() runs at render so the language is read when the ring is drawn.
  return (
    <div className="health-ring">
      <svg viewBox="0 0 150 150" width="150" height="150">
        <circle cx="75" cy="75" r={r} fill="none" stroke="var(--surface-3)" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke={color} strokeWidth="12" strokeLinecap="round" strokeDasharray={`${(value / 100) * c} ${c}`} transform="rotate(-90 75 75)" style={{ transition: 'stroke-dasharray 1s ease' }} />
      </svg>
      <div className="center">
        <div>
          <b>{value}</b>
          <div className="small muted">{t(label)}</div>
        </div>
      </div>
    </div>
  );
}
